import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  CalendarIcon,
  AlertTriangle,
  Settings,
  HelpCircle,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";

type PayType = "salary" | "hourly";

interface UnifiedPayModalProps {
  isOpen: boolean;
  onClose: () => void;
  employeeId: number;
  onSuccess?: () => void;
  currentPayType?: PayType; // passed from parent if available
  initialAction?: "pay" | "basis"; // which intent opened the modal
}

interface PayChangeResponse {
  success: boolean;
  message: string;
  timeline_entry_id?: number;
  effective_date?: string;
  error?: string;
  conflicts?: string[];
}

interface BasisSwitchResponse extends PayChangeResponse {
  old_pay_basis?: string;
  new_pay_basis?: string;
  proration_required?: boolean;
}

interface GuardrailsResponse {
  warnings: string[];
  requirements: string[];
  recommendations: string[];
}

interface EmployeeStartDateInfo {
  employee_id: number;
  start_date: string | null;
  start_date_formatted: string | null;
  has_start_date: boolean;
  can_change_start_date: boolean;
  reason_locked?: string;
}

const PAY_BASIS_OPTIONS = [
  { value: "salary", label: "Salary" },
  { value: "hourly", label: "Hourly" },
];

const SALARY_UNITS = [
  { value: "WEEKLY", label: "Per Week" },
  { value: "MONTHLY", label: "Per Month" },
  { value: "YEARLY", label: "Per Year" },
];

export const UnifiedPayModal: React.FC<UnifiedPayModalProps> = ({
  isOpen,
  onClose,
  employeeId,
  onSuccess,
  currentPayType,
  initialAction,
}) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [nextPayday, setNextPayday] = useState<Date | null>(null);
  const [earliestChangeDate, setEarliestChangeDate] = useState<Date | null>(
    null,
  );
  const [isLoadingPaydayInfo, setIsLoadingPaydayInfo] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [availableMonths, setAvailableMonths] = useState<
    { value: string; label: string; payday: string }[]
  >([]);
  const [guardrails, setGuardrails] = useState<GuardrailsResponse | null>(null);
  const [employeeStartDateInfo, setEmployeeStartDateInfo] =
    useState<EmployeeStartDateInfo | null>(null);

  const [payBasis, setPayBasis] = useState<PayType>(currentPayType || "salary");
  const [payRate, setPayRate] = useState<string>("");
  const [salaryUnit, setSalaryUnit] = useState<string>("");
  const [effectiveDate, setEffectiveDate] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [employeeStartDate, setEmployeeStartDate] = useState<string>("");

  useEffect(() => {
    if (isOpen) {
      fetchPaydayInfo();
      fetchEmployeeStartDateInfo();
      // If opened via Switch Pay Basis, preselect the opposite basis
      if (initialAction === "basis" && currentPayType) {
        setPayBasis(currentPayType === "salary" ? "hourly" : "salary");
      } else if (initialAction === "pay" && currentPayType) {
        setPayBasis(currentPayType);
      }
    }
  }, [isOpen, employeeId]);

  useEffect(() => {
    // Load guardrails when basis differs from current
    if (!isOpen) return;
    if (!currentPayType) return;
    if (payBasis === currentPayType) {
      setGuardrails(null);
      return;
    }
    fetchGuardrails(payBasis).catch(() => setGuardrails(null));
  }, [payBasis, currentPayType, isOpen]);

  useEffect(() => {
    // Regenerate available months when employee start date changes
    if (employeeStartDate && nextPayday && earliestChangeDate) {
      generateAvailableMonths(nextPayday, earliestChangeDate);
    }
  }, [employeeStartDate, nextPayday, earliestChangeDate]);

  const fetchGuardrails = async (targetBasis: PayType) => {
    const res = await ApiService.request<GuardrailsResponse>(
      EndpointKeys.GET_BASIS_GUARDRAILS,
      {
        method: "GET",
        urlParams: { employeeId: employeeId.toString() },
        queryParams: { target: targetBasis },
      },
    );
    setGuardrails(res);
  };

  const fetchEmployeeStartDateInfo = async () => {
    try {
      const res = await ApiService.request<EmployeeStartDateInfo>(
        EndpointKeys.GET_EMPLOYEE_START_DATE_INFO,
        {
          method: "GET",
          urlParams: { employeeId: employeeId.toString() },
        },
      );
      setEmployeeStartDateInfo(res);

      // Regenerate available months if we already have payday info
      if (nextPayday && earliestChangeDate) {
        generateAvailableMonths(nextPayday, earliestChangeDate);
      }
    } catch (error) {
      console.error("Failed to fetch employee start date info:", error);
      // Don't block the modal if this fails
    }
  };

  const fetchPaydayInfo = async () => {
    try {
      setIsLoadingPaydayInfo(true);
      const response = await ApiService.request<{
        next_payday: string;
        earliest_change_date: string;
        next_payday_formatted: string;
        earliest_change_date_formatted: string;
      }>(EndpointKeys.GET_NEXT_PAYDAY_INFO, {
        method: "GET",
        urlParams: { employeeId: employeeId.toString() },
      });

      const np = new Date(response.next_payday);
      const ecd = new Date(response.earliest_change_date);
      setNextPayday(np);
      setEarliestChangeDate(ecd);
      generateAvailableMonths(np, ecd);
    } catch (error) {
      // Fallback
      const today = new Date();
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      setNextPayday(endOfMonth);
      setEarliestChangeDate(today);
      generateAvailableMonths(endOfMonth, today);
    } finally {
      setIsLoadingPaydayInfo(false);
    }
  };

  const generateAvailableMonths = (np: Date, ecd: Date) => {
    const months: { value: string; label: string; payday: string }[] = [];
    const today = new Date();

    // Consider employee start date when determining the earliest available month
    let earliestDate = new Date(Math.max(ecd.getTime(), today.getTime()));

      // Use existing start date if available
      if (
        employeeStartDateInfo?.has_start_date &&
        employeeStartDateInfo.start_date
      ) {
        const startDate = parseLocalDate(employeeStartDateInfo.start_date);
        earliestDate = new Date(
          Math.max(earliestDate.getTime(), startDate.getTime()),
        );
      }
      // Use provided start date if no existing start date
      else if (employeeStartDate) {
        const startDate = parseLocalDate(employeeStartDate);
        earliestDate = new Date(
          Math.max(earliestDate.getTime(), startDate.getTime()),
        );
      }

    const startMonth = new Date(
      earliestDate.getFullYear(),
      earliestDate.getMonth(),
      1,
    );

    for (let i = 0; i < 12; i++) {
      const monthDate = new Date(
        startMonth.getFullYear(),
        startMonth.getMonth() + i,
        1,
      );
      const monthPayday = new Date(
        monthDate.getFullYear(),
        monthDate.getMonth() + 1,
        0,
      );
      months.push({
        value: format(monthDate, "yyyy-MM"),
        label: format(monthDate, "MMMM yyyy"),
        payday: format(monthPayday, "dd/MM/yyyy"),
      });
    }
    setAvailableMonths(months);
    if (months.length > 0 && !selectedMonth) {
      const first = months[0];
      setSelectedMonth(first.value);
      setEffectiveDate(`${first.value}-01`);
    }
  };

  const validate = (): boolean => {
    const v: string[] = [];

    if (!payRate || parseFloat(payRate) <= 0)
      v.push("Pay rate must be greater than zero");
    if (payBasis === "salary" && !salaryUnit)
      v.push("Please select a salary unit");
    if (!effectiveDate) v.push("Please select an effective month or date");
    if (salaryUnit === "HOURLY" && parseFloat(payRate) < 11.44)
      v.push("Hourly rate cannot be below minimum wage (£11.44/hour)");

    // Note: We don't validate against start date here anymore since we auto-adjust
    // the effective date to be the later of the selected date or start date

    // If no existing start date, require start date to be provided
    if (
      employeeStartDateInfo &&
      !employeeStartDateInfo.has_start_date &&
      !employeeStartDate
    ) {
      v.push("Employee start date is required");
    }

    if (showAdvancedSettings && earliestChangeDate && selectedDate) {
      if (selectedDate < earliestChangeDate) {
        v.push(
          `Effective date must be on or after ${format(earliestChangeDate, "dd/MM/yyyy")}`,
        );
      }
    }

    setErrors(v);
    return v.length === 0;
  };

  const isBasisSwitch = useMemo(
    () => currentPayType && payBasis !== currentPayType,
    [currentPayType, payBasis],
  );

  // Helper function to safely parse date strings without timezone issues
  const parseLocalDate = (dateString: string): Date => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  };

  // Helper function to get the actual effective date (later of selected date or start date)
  const getActualEffectiveDate = (): string => {
    if (!effectiveDate) return effectiveDate;

    let startDateToCheck: Date | null = null;

    if (
      employeeStartDateInfo?.has_start_date &&
      employeeStartDateInfo.start_date
    ) {
      startDateToCheck = parseLocalDate(employeeStartDateInfo.start_date);
    } else if (employeeStartDate) {
      startDateToCheck = parseLocalDate(employeeStartDate);
    }

    if (startDateToCheck) {
      const effectiveDateObj = parseLocalDate(effectiveDate);
      if (effectiveDateObj < startDateToCheck) {
        // Return the start date instead of the selected effective date
        const year = startDateToCheck.getFullYear();
        const month = String(startDateToCheck.getMonth() + 1).padStart(2, '0');
        const day = String(startDateToCheck.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    }

    return effectiveDate;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    setErrors([]);
    try {
      const actualEffectiveDate = getActualEffectiveDate();

      if (isBasisSwitch) {
        // Basis switch flow
        const payload = {
          new_pay_basis: payBasis,
          new_pay_rate: parseFloat(payRate),
          new_salary_unit: payBasis === "salary" ? salaryUnit : undefined,
          effective_date: actualEffectiveDate,
          reason: reason || undefined,
        };

        const res = await ApiService.request<BasisSwitchResponse>(
          EndpointKeys.CREATE_BASIS_SWITCH,
          {
            method: "POST",
            urlParams: { employeeId: employeeId.toString() },
            body: payload,
          },
        );

        if (!res.success) throw { response: { data: res } };
        toast({ title: "Pay basis updated", description: res.message });
      } else {
        // Same basis → Pay change flow
        const payChangeBody: any = {
          new_pay_rate: parseFloat(payRate),
          salary_unit: salaryUnit,
          effective_date: actualEffectiveDate,
          reason: reason || undefined,
        };

        // Include employee start date if provided
        if (employeeStartDate) {
          payChangeBody.employee_start_date = employeeStartDate;
        }

        const res = await ApiService.request<PayChangeResponse>(
          EndpointKeys.CREATE_PAY_CHANGE,
          {
            method: "POST",
            urlParams: { employeeId: employeeId.toString() },
            body: payChangeBody,
          },
        );
        if (!res.success) throw { response: { data: res } };
        toast({ title: "Pay change scheduled", description: res.message });
      }

      onSuccess?.();
      onClose();
      setPayRate("");
      setSalaryUnit("");
      setEffectiveDate("");
      setReason("");
      setEmployeeStartDate("");
      setSelectedDate(undefined);
      setSelectedMonth("");
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.conflicts) setErrors(data.conflicts);
      else if (data?.error) setErrors([data.error]);
      else setErrors(["Failed to update pay. Please try again."]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Update Pay</DialogTitle>
          <DialogDescription>
            Change pay or switch pay basis. If the basis changes, we’ll apply
            guardrails and schedule the change.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {errors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc list-inside space-y-1">
                  {errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Pay basis */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Pay Basis</Label>
              <Select
                value={payBasis}
                onValueChange={(v) => setPayBasis(v as PayType)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select basis" />
                </SelectTrigger>
                <SelectContent>
                  {PAY_BASIS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Pay Rate (£)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={payRate}
                onChange={(e) => setPayRate(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Salary unit if salary */}
          {payBasis === "salary" && (
            <div className="space-y-2">
              <Label>Salary Unit</Label>
              <Select value={salaryUnit} onValueChange={setSalaryUnit}>
                <SelectTrigger>
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  {SALARY_UNITS.map((u) => (
                    <SelectItem key={u.value} value={u.value}>
                      {u.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Employee Start Date - only show if no existing start date */}
          {employeeStartDateInfo && !employeeStartDateInfo.has_start_date && (
            <div className="space-y-2">
              <Label htmlFor="employee-start-date">Employee Start Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !employeeStartDate && "text-muted-foreground",
                    )}
                    disabled={isSubmitting}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {employeeStartDate
                      ? format(new Date(employeeStartDate), "dd/MM/yyyy")
                      : "Select start date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={
                      employeeStartDate
                        ? new Date(employeeStartDate)
                        : undefined
                    }
                    onSelect={(d) => {
                      if (d) setEmployeeStartDate(format(d, "yyyy-MM-dd"));
                    }}
                    disabled={(d) => {
                      const today = new Date();
                      const maxDate = new Date();
                      maxDate.setMonth(maxDate.getMonth() + 6); // 6 months in future
                      return (
                        d > maxDate ||
                        d <
                          new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000)
                      ); // 1 year in past
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <p className="text-sm text-muted-foreground">
                This will be the employee's first day of employment.
              </p>
            </div>
          )}

          {/* Guardrails */}
          {guardrails && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded">
              {guardrails.warnings.length > 0 && (
                <p className="text-amber-700 text-sm">
                  Warnings: {guardrails.warnings.join("; ")}
                </p>
              )}
              {guardrails.requirements.length > 0 && (
                <p className="text-amber-700 text-sm">
                  Requirements: {guardrails.requirements.join("; ")}
                </p>
              )}
              {guardrails.recommendations.length > 0 && (
                <p className="text-amber-700 text-sm">
                  Recommendations: {guardrails.recommendations.join("; ")}
                </p>
              )}
            </div>
          )}

          {/* Employee Start Date Info */}
          {employeeStartDateInfo && employeeStartDateInfo.has_start_date && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="text-blue-700 text-sm">
                Employee start date:{" "}
                {employeeStartDateInfo.start_date_formatted}
                {!employeeStartDateInfo.can_change_start_date &&
                  employeeStartDateInfo.reason_locked && (
                    <span className="ml-2 text-blue-600">
                      ({employeeStartDateInfo.reason_locked})
                    </span>
                  )}
              </p>
              <p className="text-blue-600 text-xs mt-1">
                Pay changes cannot be effective before the employee's start
                date.
              </p>
            </div>
          )}

          {/* No Start Date Info */}
          {employeeStartDateInfo && !employeeStartDateInfo.has_start_date && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-yellow-700 text-sm">
                This employee doesn't have a start date set yet.
              </p>
              <p className="text-yellow-600 text-xs mt-1">
                Please provide the employee's start date above to continue.
              </p>
            </div>
          )}

          {/* Advanced Settings Toggle */}
          <div className="flex items-center space-x-2 pt-2">
            <Switch
              id="advanced-settings"
              checked={showAdvancedSettings}
              onCheckedChange={setShowAdvancedSettings}
              disabled={isSubmitting}
            />
            <Label
              htmlFor="advanced-settings"
              className="text-sm font-normal flex items-center"
            >
              <Settings className="mr-2 h-4 w-4" />
              Advanced date settings
            </Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>
                    Most changes should be effective from the beginning of a
                    month. Use a specific date only when necessary.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Month/Date Selection */}
          <div className="space-y-2">
            <Label>
              {showAdvancedSettings ? "Effective Date" : "Effective Month"}
            </Label>

            {showAdvancedSettings ? (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !selectedDate && "text-muted-foreground",
                    )}
                    disabled={isSubmitting}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate
                      ? format(selectedDate, "dd/MM/yyyy")
                      : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => {
                      setSelectedDate(d);
                      if (d) setEffectiveDate(format(d, "yyyy-MM-dd"));
                    }}
                    disabled={(d) => {
                      let minDate = earliestChangeDate;

                        // Use existing start date if available
                        if (
                          employeeStartDateInfo?.has_start_date &&
                          employeeStartDateInfo.start_date
                        ) {
                          const startDate = parseLocalDate(
                            employeeStartDateInfo.start_date,
                          );
                          minDate = minDate
                            ? new Date(
                                Math.max(minDate.getTime(), startDate.getTime()),
                              )
                            : startDate;
                        }
                        // Use provided start date if no existing start date
                        else if (employeeStartDate) {
                          const startDate = parseLocalDate(employeeStartDate);
                          minDate = minDate
                            ? new Date(
                                Math.max(minDate.getTime(), startDate.getTime()),
                              )
                            : startDate;
                        }

                      return minDate ? d < minDate : false;
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            ) : (
              <Select
                value={selectedMonth}
                onValueChange={(v) => {
                  setSelectedMonth(v);
                  setEffectiveDate(`${v}-01`);
                }}
                disabled={isSubmitting}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  {availableMonths.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      <div className="flex flex-col">
                        <span>{m.label}</span>
                        <span className="text-xs text-muted-foreground">
                          Payday: {m.payday}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {isLoadingPaydayInfo ? (
              <p className="text-sm text-muted-foreground">
                Loading payday information...
              </p>
            ) : nextPayday ? (
              <p className="text-sm text-muted-foreground">
                {showAdvancedSettings ? (
                  <>
                    Next payday: {format(nextPayday, "dd/MM/yyyy")}
                    {earliestChangeDate &&
                      format(earliestChangeDate, "dd/MM/yyyy") !==
                        format(nextPayday, "dd/MM/yyyy") && (
                        <span>
                          {" "}
                          • Earliest change:{" "}
                          {format(earliestChangeDate, "dd/MM/yyyy")}
                        </span>
                      )}
                  </>
                ) : (
                  "Changes will be effective for the entire selected month"
                )}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Unable to load payday information
              </p>
            )}
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason (Optional)</Label>
            <Textarea
              id="reason"
              placeholder="e.g., Annual review, change of role, market adjustment..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isSubmitting}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
